import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AssignmentReturn as AssignmentReturnIcon,
  DeleteOutline as DeleteOutlineIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaContabilidad } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

const PROGRAMA = 'notacreditosuplidor';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pagosAplicadosValor = (row) => {
  const raw = row?.vplicado ?? row?.pagos_pagossup;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
};

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const opcionLabel = (s) => {
  if (!s) return '';
  const id = s.id != null ? String(s.id) : '';
  const d = (s.descrip || '').trim();
  const r = (s.rnc || '').trim();
  return `${id}-${d}-${r}`;
};

const wanomesDesdeAnomesContabilidad = (anomes) => {
  const s = String(anomes ?? '').trim();
  const digits = s.startsWith('M') || s.startsWith('m') ? s.slice(1) : s;
  if (!/^\d{6}$/.test(digits)) return null;
  return digits;
};

const zanomesDesdeFechaStr = (fechaStr) => {
  const t = (fechaStr || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  const [y, m] = t.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || m < 1 || m > 12) return null;
  return `${y}${String(m).padStart(2, '0')}`;
};

const validarMesContableAbierto = (fechaStr, wanomesAbierto) => {
  if (!wanomesAbierto) {
    return { ok: true };
  }
  const z = zanomesDesdeFechaStr(fechaStr);
  if (!z) {
    return { ok: false, message: 'Indique una fecha válida.' };
  }
  if (parseInt(z, 10) < parseInt(wanomesAbierto, 10)) {
    return {
      ok: false,
      message:
        'La fecha se encuentra en un mes cerrado (anterior al período contable abierto en varinicial: anoinicialcont / mesinicialcont).',
    };
  }
  return { ok: true };
};

const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const escapeHtml = (s) => {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const buildComprobanteNcSuplidorHtml = ({
  empresa,
  suplidor,
  fecha,
  ncNumero,
  valor,
  ncf,
  descripcion,
  aplicada,
}) => {
  const v = parseFloat(valor);
  const valorFmt = fmtMoney(Number.isFinite(v) ? v : 0);
  const fechaTxt = formatearFecha(fecha);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Nota de crédito suplidor</title>
<style>
  body { font-family: Arial, sans-serif; margin: 24px; color: #111; font-size: 13px; }
  h1 { font-size: 18px; margin-bottom: 8px; }
  .box { border: 1px solid #333; padding: 16px; margin-top: 16px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  td { padding: 6px 4px; vertical-align: top; }
  .label { width: 34%; font-weight: bold; }
  .line { border-top: 1px solid #000; flex: 1; text-align: center; padding-top: 8px; margin-top: 56px; }
</style></head><body>
  <h1>Nota de crédito de suplidor</h1>
  <p style="margin:0;color:#444;">Registro en tabla <strong>notacreditosuplidor</strong>. ${aplicada ? 'Estado: aplicada a factura (legacy).' : 'Pendiente de aplicar en registrar cuentas por pagar.'}</p>
  <div class="box">
    <table>
      <tr><td class="label">Empresa</td><td>${escapeHtml(empresa)}</td></tr>
      <tr><td class="label">Suplidor</td><td>${escapeHtml(suplidor)}</td></tr>
      <tr><td class="label">Fecha</td><td>${escapeHtml(fechaTxt)}</td></tr>
      <tr><td class="label">Nº control (ncred / id)</td><td>${escapeHtml(ncNumero)}</td></tr>
      <tr><td class="label">Monto</td><td>RD$ ${escapeHtml(valorFmt)}</td></tr>
      <tr><td class="label">NCF</td><td>${escapeHtml(ncf || '—')}</td></tr>
      <tr><td class="label">Descripción / motivo</td><td>${escapeHtml(descripcion || '—')}</td></tr>
    </table>
  </div>
  <div style="margin-top:48px;display:flex;justify-content:space-between;gap:24px;">
    <div class="line">Firma y sello — Empresa</div>
    <div class="line">Firma y sello — Suplidor</div>
  </div>
</body></html>`;
};

const NotaCreditoSuplidor = () => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA);
  const puedeModificar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const nombreEmpresa =
    selectedCompany?.name ||
    selectedCompany?.empresa ||
    (typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '') ||
    'Empresa';

  const [suplidores, setSuplidores] = useState([]);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [cargandoSupl, setCargandoSupl] = useState(false);
  const [filas, setFilas] = useState([]);
  const [filaSel, setFilaSel] = useState(null);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);

  const [fecha, setFecha] = useState(() => fechaLocalHoy());
  const [valorStr, setValorStr] = useState('');
  const [ncf, setNcf] = useState('');
  const [descripcion, setDescripcion] = useState('');

  const [wanomesContableAbierto, setWanomesContableAbierto] = useState(null);

  const wanomesDesdeLogin = useMemo(
    () => wanomesDesdeAnomesContabilidad(selectedCompany?.anomescontabilidad),
    [selectedCompany?.anomescontabilidad]
  );

  const cargarVarinicialCont = useCallback(async () => {
    if (!puedeOperarDb) return;
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_obtenervarinicialcont`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.wanomes) {
        setWanomesContableAbierto(String(data.wanomes));
      } else {
        if (!wanomesDesdeLogin) {
          setWanomesContableAbierto(null);
        }
        if (data.Error) {
          showWarning(data.Error);
        }
      }
    } catch {
      if (!wanomesDesdeLogin) {
        setWanomesContableAbierto(null);
      }
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, wanomesDesdeLogin, showWarning]);

  useEffect(() => {
    if (wanomesDesdeLogin) {
      setWanomesContableAbierto(wanomesDesdeLogin);
      return;
    }
    setWanomesContableAbierto(null);
    if (puedeLeer && puedeOperarDb) {
      cargarVarinicialCont();
    }
  }, [wanomesDesdeLogin, selectedCompany?.db_empresa, puedeLeer, puedeOperarDb, cargarVarinicialCont]);

  const validacionFecha = useMemo(
    () => validarMesContableAbierto(fecha, wanomesContableAbierto),
    [fecha, wanomesContableAbierto]
  );

  const cargarSuplidores = useCallback(async () => {
    if (!puedeOperarDb) return;
    setCargandoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarsuplidores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar suplidores');
        setSuplidores([]);
        return;
      }
      setSuplidores(data.suplidores || []);
    } catch {
      showError('Error de conexión al cargar suplidores');
      setSuplidores([]);
    } finally {
      setCargandoSupl(false);
    }
  }, [puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const cargarLista = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargandoLista(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        limite: 150,
      };
      if (suplidorSel?.id != null) {
        body.suplidor_id = String(suplidorSel.id);
      }
      const response = await fetch(`${servidor}/contabilidadbackend_listarnotacreditosuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setFilas(data.notas || []);
      } else {
        setFilas([]);
        showError(data.Error || 'No se pudo cargar el listado');
      }
    } catch {
      setFilas([]);
      showError('Error de conexión al cargar el listado');
    } finally {
      setCargandoLista(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, suplidorSel, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarSuplidores();
    }
  }, [puedeLeer, puedeOperarDb, selectedCompany?.db_empresa, cargarSuplidores]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarLista();
    }
  }, [puedeLeer, puedeOperarDb, suplidorSel, cargarLista]);

  useEffect(() => {
    if (filaSel?.id == null) return;
    if (!filas.some((r) => r.id === filaSel.id)) {
      setFilaSel(null);
    }
  }, [filas, filaSel]);

  const abrirVentanaImpresion = useCallback(
    (html) => {
      const w = window.open('', '_blank', 'width=820,height=900');
      if (!w) {
        showError('Permita ventanas emergentes para imprimir');
        return;
      }
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => {
        w.print();
      }, 250);
    },
    [showError]
  );

  const imprimirSeleccion = useCallback(() => {
    const row = filaSel;
    if (!row) {
      showError('Seleccione un documento del listado.');
      return;
    }
    const html = buildComprobanteNcSuplidorHtml({
      empresa: nombreEmpresa,
      suplidor: (row.nombre_suplidor || '').trim() || String(row.suplidor || ''),
      fecha: row.fecha,
      ncNumero: String(row.ncred || row.id || ''),
      valor: row.precio,
      ncf: row.ncf,
      descripcion: row.descripcion,
      aplicada: String(row.status || '').toUpperCase() === 'S',
    });
    abrirVentanaImpresion(html);
  }, [filaSel, nombreEmpresa, showError, abrirVentanaImpresion]);

  const handleEliminarNota = useCallback(
    async (row, ev) => {
      if (ev) {
        ev.stopPropagation();
        ev.preventDefault();
      }
      if (!puedeModificar) {
        showError('No tiene permiso para modificar');
        return;
      }
      if (!row?.id) {
        return;
      }
      if (String(row.status || '').toUpperCase() === 'S') {
        showError('No se puede eliminar: la nota de crédito está aplicada.');
        return;
      }
      const pagosAplicados = pagosAplicadosValor(row);
      if (pagosAplicados > 0) {
        showError(`No se puede eliminar: la factura aplicada tiene pagos por RD$ ${fmtMoney(pagosAplicados)}.`);
        return;
      }
      const idStr = String(row.id);
      const msg =
        `¿Eliminar la nota de crédito (id ${idStr}, ncred ${row.ncred != null ? String(row.ncred) : '—'}) del suplidor "${(row.nombre_suplidor || row.suplidor || '').trim() || '—'}"?\n\n` +
        'No podrá eliminarla si la factura CxP vinculada tiene pagos registrados.';
      if (!window.confirm(msg)) return;

      if (!selectedCompany?.db_empresa) {
        showError('Servidor o empresa no disponible');
        return;
      }
      setEliminandoId(row.id);
      try {
        const response = await fetch(`${servidor}/contabilidadbackend_eliminarnotacreditosuplidor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            nota_id: row.id,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          showError(data.Error || 'No se pudo eliminar la nota de crédito');
          return;
        }
        showSuccess(data.mensaje || 'Nota de crédito eliminada');
        setFilaSel((prev) => (prev?.id === row.id ? null : prev));
        await cargarLista();
      } catch {
        showError('Error de conexión al eliminar');
      } finally {
        setEliminandoId(null);
      }
    },
    [puedeModificar, servidor, selectedCompany, usuarioLog, claveLog, showError, showSuccess, cargarLista]
  );

  const handleGuardar = useCallback(async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    if (!selectedCompany?.db_empresa) {
      showError('Servidor o empresa no disponible');
      return;
    }
    const vFecha = validarMesContableAbierto(fecha, wanomesContableAbierto);
    if (!vFecha.ok) {
      showError(vFecha.message);
      return;
    }
    if (!suplidorSel?.id) {
      showError('Seleccione un suplidor');
      return;
    }
    const rawValor = (valorStr || '').trim().replace(',', '.');
    const valorNum = parseFloat(rawValor);
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      showError('Indique un monto válido mayor que cero');
      return;
    }
    setGuardando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        suplidor_id: String(suplidorSel.id),
        fecha,
        precio: valorNum,
        itbis: 0,
        ncf: (ncf || '').trim(),
        descripcion: (descripcion || '').trim(),
        usuario: usuarioLog || '',
        password: claveLog || '',
      };
      const response = await fetch(`${servidor}/contabilidadbackend_guardarnotacreditosuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo guardar');
        return;
      }
      const idNuevo = data.id != null && data.id !== '' ? String(data.id) : '';
      const ncred = data.ncred != null && data.ncred !== '' ? String(data.ncred) : idNuevo;
      showSuccess(
        idNuevo
          ? `${data.mensaje || 'Nota de crédito registrada'} · Nº control (ncred): ${ncred}`
          : data.mensaje || 'Nota de crédito registrada'
      );
      setValorStr('');
      setNcf('');
      setDescripcion('');
      await cargarLista();
    } catch {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  }, [
    puedeModificar,
    servidor,
    selectedCompany,
    fecha,
    wanomesContableAbierto,
    suplidorSel,
    valorStr,
    ncf,
    descripcion,
    usuarioLog,
    claveLog,
    showError,
    showSuccess,
    cargarLista,
  ]);


  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const navyBtnSx = {
    height: 38,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Nota de crédito de suplidor"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Registro en notacreditosuplidor` : 'Registro en notacreditosuplidor'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «notacreditosuplidor» en roles para registrar notas de crédito de suplidor.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Nota de crédito de suplidor"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Registro en notacreditosuplidor` : 'Registro en notacreditosuplidor'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card elevation={0} sx={{ ...cardShellSx, maxWidth: 560, width: '100%', p: { xs: 3, md: 4 } }}>
            <Alert severity="info">
              Seleccione una empresa con base de datos asignada para continuar.
            </Alert>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Nota de crédito de suplidor"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Registro en notacreditosuplidor`
            : 'Registro en notacreditosuplidor'
        }
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AssignmentReturnIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del documento
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={imprimirSeleccion}
              disabled={!filaSel}
              sx={navyBtnSx}
              className="no-print"
            >
              Imprimir selección
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={suplidores}
                  loading={cargandoSupl}
                  getOptionLabel={(o) => opcionLabel(o)}
                  value={suplidorSel}
                  onChange={(_, v) => setSuplidorSel(v)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      placeholder="id-nombre-rnc"
                      size="small"
                      helperText="Maestros tipo suplidor (maestcta)"
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  error={!validacionFecha.ok}
                  helperText={!validacionFecha.ok ? validacionFecha.message : 'Período contable (varinicial / sesión)'}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Monto (precio)"
                  size="small"
                  value={valorStr}
                  onChange={(e) => setValorStr(e.target.value)}
                  placeholder="0.00"
                  inputProps={{ inputMode: 'decimal' }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="NCF"
                  size="small"
                  value={ncf}
                  onChange={(e) => setNcf(e.target.value.slice(0, 15))}
                  helperText="Hasta 15 caracteres (campo ncf en base de datos)"
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Descripción / motivo"
                  size="small"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value.slice(0, 100))}
                  helperText="Se guarda en tipodefactura (100 caracteres)"
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2, mb: 1 }} className="no-print">
              Tras guardar, el sistema asigna <strong>ncred</strong> igual al <strong>id</strong> del registro para
              poder aplicar la nota al registrar cuentas por pagar (flujo legacy). Cuando se aplica a una factura, el
              estado pasa a <strong>S</strong> y <strong>ncred</strong> puede actualizarse al número de factura.
            </Alert>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={!puedeModificar || guardando}
                  onClick={handleGuardar}
                  sx={{ ...navyBtnSx, width: '100%' }}
                >
                  Guardar nota de crédito
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box sx={filterHeaderSx}>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Registros{suplidorSel ? ' (suplidor seleccionado)' : ' (todos, límite 150)'}
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem', mb: 1.5 }}>
            Use el icono de papelera en cada fila para eliminar esa nota (solo si está pendiente y el suplidor no tiene
            pagos en <strong>pagossup</strong>).
          </Typography>
          {cargandoLista ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, borderColor: FP.color.border }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Fecha</TableCell>
                    <TableCell>Suplidor</TableCell>
                    <TableCell>ncred</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>NCF</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Factura aplicada</TableCell>
                    <TableCell align="right">Pagos factura aplicada</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="center" sx={{ width: 56 }}>
                      Eliminar
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Typography variant="body2" color="text.secondary">
                          No hay registros.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filas.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        selected={filaSel?.id === row.id}
                        onClick={() => setFilaSel(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Radio
                            checked={filaSel?.id === row.id}
                            onChange={() => setFilaSel(row)}
                            value={String(row.id)}
                            inputProps={{ 'aria-label': `Seleccionar id ${row.id}` }}
                          />
                        </TableCell>
                        <TableCell>{formatearFecha(row.fecha)}</TableCell>
                        <TableCell>{row.nombre_suplidor || row.suplidor}</TableCell>
                        <TableCell>{row.ncred != null ? String(row.ncred) : '—'}</TableCell>
                        <TableCell align="right">${fmtMoney(row.precio)}</TableCell>
                        <TableCell>{row.ncf || '—'}</TableCell>
                        <TableCell>
                          {String(row.status || '').toUpperCase() === 'S' ? (
                            <Typography component="span" variant="body2" color="success.main">
                              Aplicada
                            </Typography>
                          ) : (
                            <Typography component="span" variant="body2" color="text.secondary">
                              Pendiente
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {(row.nfactura_aplicada || '').trim() || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {pagosAplicadosValor(row) > 0 ? `$${fmtMoney(pagosAplicadosValor(row))}` : '—'}
                        </TableCell>
                        <TableCell>{(row.descripcion || '').slice(0, 80)}</TableCell>
                        <TableCell align="center" padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Tooltip
                            title={
                              pagosAplicadosValor(row) > 0
                                ? `Factura aplicada con pagos por RD$ ${fmtMoney(pagosAplicadosValor(row))} — no se puede eliminar`
                                : String(row.status || '').toUpperCase() === 'S'
                                  ? 'Nota aplicada — no se puede eliminar'
                                : puedeModificar
                                  ? 'Eliminar nota de crédito'
                                  : 'Sin permiso para eliminar'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Eliminar nota de crédito ${row.id}`}
                                disabled={
                                  !puedeModificar ||
                                  eliminandoId != null ||
                                  String(row.status || '').toUpperCase() === 'S' ||
                                  pagosAplicadosValor(row) > 0
                                }
                                onClick={(e) => handleEliminarNota(row, e)}
                              >
                                {eliminandoId === row.id ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : (
                                  <DeleteOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default NotaCreditoSuplidor;
